// imports
import { AnyAction, ThunkDispatch, createAsyncThunk } from '@reduxjs/toolkit';
import { IAPIQuests } from '../../../../api';
import { IQuestState } from '../../../../web3/types';
import { DATE_UTILS, QUEST_UTILS } from '../../../../../common/utils';
import { Quest, QuestType, QuestStatus } from '../../../../../common/types';

// interfaces / types / consts
interface Quests { [key: string]: Quest }
interface IQuestProgress {
  data: Quests;
  unavailableIds: string[];
  campaignProgression: number;
}
interface IQuestProgressParams {
  metaQuests: Quests;
  unavailableQuests: Quests;
  campaignProgression: number;
  inProgressQuests: IQuestState[];
}
type IDispatchAction = ThunkDispatch<unknown, unknown, AnyAction>;
const COMPLETE_QUEST = createAsyncThunk(
  'user/quests/complete',
  (questId: string) => Promise.resolve(questId)
);
const SET_QUEST_TIMER = createAsyncThunk(
  'ui/questsProgress/update',
  (body: { id: string; time: string; progress: number }) => Promise.resolve(body)
);

// exports - thunks
export const completeQuest = COMPLETE_QUEST;
export const setQuestTimer = SET_QUEST_TIMER;

// exports - schedulers
export const scheduleCompleteQuestTimeout = (id: string, endTime: number, dispatch: IDispatchAction): void => {
  setTimeout(() => dispatch(COMPLETE_QUEST(id)), DATE_UTILS.getRemainingMS(endTime));
};
export const scheduleQuestTimer = (id: string, startTime: number, endTime: number, dispatch: IDispatchAction): void => {
  const remainingTime = DATE_UTILS.formatRemainingTime(endTime);
  dispatch(SET_QUEST_TIMER({ id, time: remainingTime, progress: QUEST_UTILS.getQuestProgress(startTime, endTime) }));
  let timeout = 1;
  if (remainingTime === '0s') return;
  if (remainingTime.includes('d') && remainingTime.includes('h')) {
    timeout = 60 * 60;
  } else if (remainingTime.includes('h') && remainingTime.includes('m')) {
    timeout = 60;
  }
  setTimeout(() => scheduleQuestTimer(id, startTime, endTime, dispatch), timeout * 1000);
};

// exports - parsers
export const parseQuests = (quests: IAPIQuests, isCampaign = false): { [key: string]: Quest } => {
  const type = isCampaign ? QuestType.Campaign : QuestType.Side;
  return Object.values(quests).reduce((acc, quest) => {
    const { id } = quest;
    acc[id] = {
      id,
      type,
      crew: [0, 0, 0],
      name: quest.title,
      points: quest.points,
      pinId: quest.pin_id || '',
      description: quest.text_intro,
      messageMid: quest.message_mid,
      messageError: quest.message_fail,
      duration: Number(quest.duration),
      messageSuccess: quest.message_success,
      status: isCampaign ? QuestStatus.locked : QuestStatus.available,
      traits: quest.traits.map((traitsArr) => traitsArr.map((trait) => trait.id)),
      cost: quest.costs.map((cost) => ({ itemId: cost.cost_type, amount: Number(cost.amount) })),
      rewards: quest.rewards.map((reward) => ({
        itemId: reward.reward_type,
        amount: Number(reward.amount),
        minChance: reward.min_chance,
        maxChance: reward.max_chance,
        minThreshold: reward.min_threshold,
        maxThreshold: reward.max_threshold
      }))
    };
    return acc;
  }, {} as { [key: string]: Quest });
};
export const parseQuestProgress = (params: IQuestProgressParams, dispatch: IDispatchAction): IQuestProgress => {
  const {
    metaQuests, unavailableQuests, inProgressQuests, campaignProgression
  } = params;
  const data: Quests = { ...JSON.parse(JSON.stringify(metaQuests)), ...unavailableQuests };
  // setup in-progress quests
  inProgressQuests.forEach((inProgressQuest) => {
    const id = inProgressQuest.questId;
    const endTime = Number(inProgressQuest.endTime);
    const startTime = Number(inProgressQuest.startTime);
    data[id].endTime = endTime;
    data[id].startTime = startTime;
    data[id].status = QUEST_UTILS.getStatus(data[id]);
    data[id].pinId = data[id].pinId || inProgressQuest.pinId;
    data[id].crew = inProgressQuest.heroTokens.map((tokenId) => Number(tokenId));
    data[id].rewardChances = inProgressQuest.rewardChances.map((rewardChance) => Number(rewardChance));
    scheduleCompleteQuestTimeout(id, endTime, dispatch);
    scheduleQuestTimer(id, startTime, endTime, dispatch);
  });
  // setup campaigns
  for (let i = 1; i <= campaignProgression; i += 1) {
    const campaignId = `camp_${`${i}`.padStart(3, '0')}`;
    delete data[campaignId];
  }
  const currCampaignId = `camp_${`${campaignProgression + 1}`.padStart(3, '0')}`;
  if (data[currCampaignId]) { data[currCampaignId].status = QUEST_UTILS.getStatus(data[currCampaignId]); }
  return { data, campaignProgression, unavailableIds: Object.keys(unavailableQuests) };
};
// exports - default
export default {
  completeQuest,
  setQuestTimer,
  parseQuestProgress,
  scheduleQuestTimer,
  scheduleCompleteQuestTimeout
};
